import React, { useState, useCallback, useMemo } from 'react';
import { useNavigationType } from 'react-router';
import { FormattedMessage } from 'react-intl';
import { useMessage } from '@tmapy/intl';
import { useDispatch, useSelector } from '@tmapy/redux';

import { DangerAlert, InfoAlert, WaitingAlert, Table } from '@tmapy/style-guide';

import { useOrdersListResource } from '../../openapi/operations/ordersList';

import { MainContent } from '../../app/components/MainContent';
import { PageTitle } from '../../app/components/PageTitle';
import { TableWithAsideLayout } from '../../app/components/TableWithAsideLayout';
import { LoadNextItemsBar } from '../../app/components/LoadNextItemsBar';
import { FilterBtn } from '../../app/components/FilterBtn';
import { QUERY_STATE } from '../../constants';
import { SSE_TYPE, useAuth, useSSE } from '../../hooks';
import { usableItemsForFormType } from '../constants';
import { OrderFilter } from './OrderFilter';
import { OrderListItem } from './OrderListItem';
import { orderListMessages } from './orderMessages';

export const OrderList: React.FC = () => {
  useSSE(SSE_TYPE.ORDERS);
  const dispatch = useDispatch();
  const auth = useAuth();
  const formatMessage = useMessage();
  const [isFilterOpen, setFilterOpen] = useState(false);
  const navigationType = useNavigationType();
  const filters = useSelector((state) => state.filter.orderList);
  const { customerType } = useSelector((state) => state.app.config);
  const usableItems = usableItemsForFormType[customerType];

  const { fetchMore, refetch, queryState, data } = useOrdersListResource(
    filters,
    navigationType !== 'PUSH',
  );

  const handleRefetch = useCallback(() => {
    refetch();
  }, [refetch]);

  const orderList = data?.results ?? [];
  const orderCount = data?.count;

  const acl = useMemo(
    () => ({
      ordersActionHiddenCreator: auth.hasPermission('Orders', 'action.hidden_creator', data),
    }),
    [auth, data],
  );

  function handleFilterBtnClick() {
    setFilterOpen(!isFilterOpen);
  }

  function handleNextDocsBtnClick() {
    if (data?.next) {
      fetchMore(data.next);
    }
  }

  const isFiltered = Object.values(filters).some((filter) => filter);

  return (
    <TableWithAsideLayout
      asideLabel={formatMessage(orderListMessages.orderListFiltrLabel)}
      isAsideOpen={isFilterOpen}
      onAsideClose={handleFilterBtnClick}
    >
      <MainContent width='fit' scrollRestorationPrefix='orderlist'>
        <article className='sg-u-vs-2'>
          <header className='sg-a-d-f sg-a-jc-sb sg-a-ai-b sg-a-cg-2'>
            <PageTitle>
              <FormattedMessage {...orderListMessages.orderListPageTitle} />
            </PageTitle>
            <FilterBtn isFiltered={isFiltered} onClick={handleFilterBtnClick} />
          </header>
          {queryState === QUERY_STATE.WAITING && orderList.length < 1 && (
            <WaitingAlert>
              <FormattedMessage {...orderListMessages.orderListStateWaiting} />
            </WaitingAlert>
          )}
          {queryState === QUERY_STATE.FAIL && orderList.length < 1 && (
            <DangerAlert>
              <FormattedMessage {...orderListMessages.orderListStateFail} />
            </DangerAlert>
          )}
          {queryState === QUERY_STATE.SUCCESS && orderList.length < 1 && (
            <>
              {isFiltered ? (
                <InfoAlert>
                  <FormattedMessage {...orderListMessages.orderListStateFilteredEmpty} />
                </InfoAlert>
              ) : (
                <InfoAlert>
                  <FormattedMessage {...orderListMessages.orderListStateEmpty} />
                </InfoAlert>
              )}
            </>
          )}
          {orderList.length > 0 && (
            <div className='sg-u-box sg-a-px-2 sg-a-pb-2 sg-u-vs-2'>
              <div className='sg-a-mx--2'>
                <Table isClickable>
                  <thead>
                    <tr>
                      <th className='sg-a-ta-c'>
                        <div>
                          <FormattedMessage {...orderListMessages.orderListColumnNumber} />
                        </div>
                        {usableItems?.includes('state') && (
                          <div>
                            <FormattedMessage {...orderListMessages.orderListColumnState} />
                          </div>
                        )}
                      </th>
                      {usableItems?.includes('selection_type') && (
                        <th>
                          <FormattedMessage {...orderListMessages.orderListColumnType} />
                        </th>
                      )}
                      {usableItems?.includes('dmvs_id_podani') && (
                        <th>
                          <FormattedMessage {...orderListMessages.orderListColumnIdPodani} />
                        </th>
                      )}
                      {usableItems?.includes('dmvs_source') && (
                        <>
                          <th>
                            <FormattedMessage {...orderListMessages.orderListColumnImportType} />
                          </th>
                        </>
                      )}
                      {usableItems?.includes('is_public') && (
                        <th>
                          <FormattedMessage {...orderListMessages.orderListIsPublicTitle} />
                        </th>
                      )}
                      {usableItems?.includes('origin') && !acl.ordersActionHiddenCreator && (
                        <th>
                          <FormattedMessage {...orderListMessages.orderListColumnOrigin} />
                        </th>
                      )}
                      {usableItems?.includes('creator') && !acl.ordersActionHiddenCreator && (
                        <th>
                          <FormattedMessage {...orderListMessages.orderListColumnCreator} />
                        </th>
                      )}
                      {usableItems?.includes('publishsets') && (
                        <>
                          <th>
                            <FormattedMessage {...orderListMessages.orderListColumnPublishsets} />
                          </th>
                          <th>
                            <FormattedMessage
                              {...orderListMessages.orderListColumnRequiredFormats}
                            />
                          </th>
                        </>
                      )}
                      {usableItems?.includes('created') && (
                        <th className='sg-a-ta-r sg-a-ws-nw'>
                          <FormattedMessage {...orderListMessages.orderListColumnCreated} />
                        </th>
                      )}
                      {usableItems?.includes('published') && (
                        <th className='sg-a-ta-r sg-a-ws-nw'>
                          <FormattedMessage {...orderListMessages.orderListColumnPublished} />
                        </th>
                      )}
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {orderList.map((orderItem) => (
                      <OrderListItem
                        key={orderItem.id}
                        {...orderItem}
                        refetchData={handleRefetch}
                      />
                    ))}
                  </tbody>
                </Table>
              </div>
              <LoadNextItemsBar
                count={orderList.length}
                total={orderCount!}
                queryState={queryState}
                onNextBtnClick={handleNextDocsBtnClick}
              />
            </div>
          )}
        </article>
      </MainContent>
      <OrderFilter filters={filters} dispatch={dispatch} />
    </TableWithAsideLayout>
  );
};
